<template>
    <div class="create-workflow">
      <h1>Create a Workflow</h1>
      <form @submit.prevent="submitForm">
        <div class="form-group">
          <label for="name">Name</label>
          <input
            type="text"
            id="name"
            v-model="workflow.name"
            @input="validateName"
            :class="{ 'is-invalid': validationErrors.includes('name') }"
            required
            class="form-control"
          />
          <div v-if="validationErrors.includes('name')" class="error-feedback">Name is required.</div>
        </div>
        <div class="form-group">
          <label for="status">Status</label>
          <select id="status" v-model="workflow.status" class="form-control">
            <option value="contract">Contract</option>
            <option value="to be started">To be started</option>
            <option value="ongoing">Ongoing</option>
            <option value="finished">Finished</option>
          </select>
        </div>
        <div class="form-group">
          <label for="start_date">Start Date</label>
          <input type="date" id="start_date" v-model="workflow.start_date" class="form-control" />
        </div>
        <div class="form-group">
          <label for="end_date">End Date</label>
          <input type="date" id="end_date" v-model="workflow.end_date" class="form-control" />
        </div>
        <div class="form-group">
          <label for="project_id">Project</label>
          <select
            id="project_id"
            v-model="workflow.project_id"
            required
            class="form-control"
            :disabled="isProjectFixed"
          >
            <option value="" disabled>Select a project</option>
            <option v-for="project in projects" :key="project.id" :value="project.id">
              {{ project.name }}
            </option>
          </select>
        </div>
        <button type="submit" class="submit-button" :disabled="loading">
          <span v-if="loading" class="spinner"></span>
          <span v-else>Create Workflow</span>
        </button>
      </form>
      <div v-if="loading" class="overlay">
        <div class="spinner"></div>
      </div>
    </div>
  </template>
  
  <script>
  import { useToast } from "vue-toastification";
  import { API_ENDPOINTS } from "@/services/config";
  import apiService from "@/services/apiService";
  import { mapState } from 'vuex';
  
  const FORM_CACHE_KEY = "workflow_form";
  const CACHE_EXPIRATION = 10 * 60 * 1000; // 10 minutes in milliseconds
  
  export default {
    data() {
      const today = new Date();
      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(today.getFullYear() + 1);
  
      return {
        workflow: {
          name: "",
          status: "",
          start_date: today.toISOString().split('T')[0],
          end_date: oneYearFromNow.toISOString().split('T')[0],
          project_id: "",
        },
        projects: [],
        validationErrors: [],
        loading: false,
        isProjectFixed: false,
      };
    },
    mounted() {
      const { projectId } = this.$route.query;
      if (projectId) {
        this.workflow.project_id = Number(projectId);
        this.isProjectFixed = true;
      }

    },
    setup() {
      const toast = useToast();
      return { toast };
    },
    created() {
      this.loadCachedFormData();
      this.fetchProjects();
    },
    computed: {
      ...mapState(['loggedInUser']), 
    },
    methods: {
      // Load cached form data if available and not expired
      loadCachedFormData() {
        const cachedData = JSON.parse(localStorage.getItem(FORM_CACHE_KEY));
        if (cachedData) {
          const { data, timestamp } = cachedData;
          const now = Date.now();
  
          if (now - timestamp < CACHE_EXPIRATION) {
            this.workflow = data;
          } else {
            localStorage.removeItem(FORM_CACHE_KEY); // Clear expired data
          }
        }
      },
  
      // Save form data to cache
      cacheFormData() {
        const cache = {
          data: this.workflow,
          timestamp: Date.now(),
        };
        localStorage.setItem(FORM_CACHE_KEY, JSON.stringify(cache));
      },
  
      async fetchProjects() {
        try {
          const response = await apiService.fetchResources(API_ENDPOINTS.projects, this.loggedInUser.entra_id);
          this.projects = response;
        } catch (error) {
          console.error("Error fetching projects:", error);
        }
      },
  
      validateName() {
        if (!this.workflow.name) {
          if (!this.validationErrors.includes("name")) {
            this.validationErrors.push("name");
          }
        } else {
          this.validationErrors = this.validationErrors.filter((error) => error !== "name");
        }
      },
  
      async submitForm() {
        this.validateName();
        if (this.validationErrors.length > 0) {
          this.toast.error("Please fill out required fields.");
          return;
        }
  
        this.loading = true;
  
        try {
          const response = await apiService.createResource(API_ENDPOINTS.workflows, this.workflow, this.loggedInUser.entra_id);
          this.toast.success("Workflow created successfully!");
          localStorage.removeItem(FORM_CACHE_KEY); // Clear cache after successful submission
          this.$router.push({ name: "WorkflowDetailPage", params: { id: response.id } });
        } catch (error) {
          this.toast.error("Failed to create workflow.");
          console.error("Failed to submit workflow details:", error);
        } finally {
          this.loading = false;
        }
      },
    },
    watch: {
      // Automatically cache form data when it changes
      workflow: {
        handler() {
          this.cacheFormData();
        },
        deep: true,
      },
    },
  };
  </script>
  
  
  <style scoped lang="scss">
  @import "@/assets/main.scss";
  
  .create-workflow {
    max-width: 800px;
    margin: 20px auto;
    padding: 20px;
    background: $secondary-color;
    border-radius: 8px;
    box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
  
    h1 {
      margin-bottom: 2rem;
      text-align: center;
    }
    .submit-button {
      margin-top: 20px;
      width: 100%;
    }
  }
  </style>
  